import React from 'react'
import type { PublicProfileCollection, PublicProfileProduct } from 'apollo-gql'
import { trackProfileCollectionPageViewed } from 'lib/tracking/avo/trackPageView'

import NavigationTagsBreadcrumbs from 'components/NavigationTagsBreadcrumbs/NavigationTagsBreadcrumbs'
import ProfileProductGrid from 'components/ProfileProductGrid/ProfileProductGrid'
import Spinner from 'components/Spinner/Spinner'
import Pagination from 'components/UI/Pagination/Pagination'
import Wrapper from 'components/Wrapper/Wrapper'
import useEffectOnce from 'hooks/useEffectOnce'
import useFeatureFlags from 'hooks/useFeatureFlags'
import usePath from 'hooks/usePath'
import useRouter from 'hooks/useRouter'

import styles from './CollectionPage.module.css'

type CollectionPageProps = {
  profileCollection: PublicProfileCollection
  loading: boolean
  collectionSlug: string
}

const CollectionPage: React.FC<CollectionPageProps> = ({
  profileCollection,
  collectionSlug,
  loading,
}) => {
  const { browsability } = useFeatureFlags()
  const router = useRouter()
  const { page } = router.query
  const PAGE_SIZE = 24
  const currentPage = Number(page || 1)
  const totalPages = Math.ceil(
    profileCollection?.profileProducts?.totalCount / PAGE_SIZE,
  )
  const profileProductEdgeNodes = (
    profileCollection?.profileProducts?.edges || []
  ).map((edge) => edge.node) as PublicProfileProduct[]
  const path = usePath()

  useEffectOnce(() => {
    trackProfileCollectionPageViewed({
      pageSlug: collectionSlug,
      path,
      profileCollection,
    })
  })

  const tag = profileCollection?.mainTag

  return (
    <div className={styles.wrapper}>
      <Wrapper edgeToEdge>
        {loading && !profileCollection?.profileProducts ? (
          <div className={styles.spinner}>
            <Spinner centered />
          </div>
        ) : (
          <>
            {browsability && tag && (
              <div className={styles.breadcrumbs}>
                <NavigationTagsBreadcrumbs tag={tag} paddingHorizontal="wide" />
              </div>
            )}

            <div data-cy="profileProductList">
              <ProfileProductGrid
                profileProducts={profileProductEdgeNodes}
                collectionSlug={collectionSlug}
              />
              <Pagination
                pathname={`/profiles/${collectionSlug}`}
                currentPage={currentPage}
                totalPages={totalPages}
              />
            </div>
          </>
        )}
      </Wrapper>
    </div>
  )
}

export default CollectionPage
